import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isNil } from 'lodash'

/**
 * All of an org's connections for one knowledge-base provider, plus which one
 * the operator is looking at and that one's ingest state (QCER-1027).
 *
 * Selection policy: keep the selected connection across a reload whenever it
 * still exists, otherwise fall back to the first row. That is what makes a
 * single-connection org cost no extra clicks — its only connection is selected
 * before the first paint.
 *
 * @param {number} props.organizationId
 * @param {Function} props.fetchConnections thunk factory returning an ARRAY; must RETHROW load errors
 * @param {Function} props.fetchSyncState thunk factory receiving { connectionId, connection };
 *   must resolve failures to null. The full row rides along so a multi-provider
 *   caller can route the fetch to the selected connection's own provider.
 */
export const useConnectionListPanel = ({ organizationId, fetchConnections, fetchSyncState }) => {
  const dispatch = useDispatch()
  const [connections, setConnections] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  // Mirrors selectedId for reads that happen AFTER an await. Callers capture
  // `reload` before their own mutation request, so by the time it runs the
  // operator may have selected a different connection — resolving against the
  // closed-over state would silently drag them back to the old one.
  const selectedIdRef = useRef(null)
  // The detail pane shows the create form instead of a selected connection.
  const [creating, setCreating] = useState(false)
  // undefined = fetching, null = fetch failed, object = the persisted state.
  const [syncState, setSyncState] = useState(undefined)

  const selectId = (connectionId) => {
    selectedIdRef.current = connectionId
    setSelectedId(connectionId)
  }
  // Monotonic id of the latest sync-state fetch: it runs on mount, after
  // Save/Activate, and on every selection change, so an older in-flight
  // response must not overwrite a newer one — including one for a connection
  // the operator has already navigated away from.
  const syncStateRequest = useRef(0)
  // Same idea for the list itself.
  const listRequest = useRef(0)
  // Whether this panel is still on screen. The operator can close the modal
  // while a fetch is in flight (switch rows, then close), and every setState
  // below has to be able to ask.
  const mounted = useRef(true)
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const loadSyncState = (connection, isMounted) => {
    // Deliberately NOT awaited by the caller: the config form must never be
    // gated on this diagnostic call — the table hydrates on its own while the
    // form is already usable. The thunk resolves failures to null (rendered as
    // a distinct "couldn't load" line), so no rejection can escape.
    const requestId = syncStateRequest.current + 1
    syncStateRequest.current = requestId
    if (isMounted()) {
      setSyncState(undefined)
    }
    dispatch(fetchSyncState({ connectionId: connection.id, connection })).then((state) => {
      // Only the latest request may land — a stale response from before a
      // Save/Activate reload, or from a previously selected connection, would
      // otherwise clobber the fresh one.
      if (isMounted() && requestId === syncStateRequest.current) {
        setSyncState(state)
      }
    })
  }

  /**
   * Reload the list and re-resolve the selection.
   *
   * @param {Function} isMounted guard so a response racing an unmount is dropped
   * @param {number} preferredId select this connection if the reload returns it
   *   (a fresh create — its row does not exist in state yet, so the caller
   *   cannot select it before the list lands)
   */
  const reload = async (isMounted = () => mounted.current, preferredId = null) => {
    const requestId = listRequest.current + 1
    listRequest.current = requestId
    try {
      const rows = await dispatch(fetchConnections({ organizationId }))
      // Two reloads can overlap (a save racing a removal); the older response
      // must not repaint the list over the newer one.
      if (!isMounted() || requestId !== listRequest.current) {
        return
      }
      setConnections(rows)
      setLoadError(false)
      const wanted = isNil(preferredId) ? selectedIdRef.current : preferredId
      const resolved = rows.find((row) => row.id === wanted) ?? rows[0]
      selectId(resolved?.id ?? null)
      if (!isNil(resolved)) {
        loadSyncState(resolved, isMounted)
      }
      // Leave the create form only once the connection it created has actually
      // come back in the list. A FAILED create also reloads, and on an org that
      // already holds connections the selection would resolve to the previous
      // row — closing the form and throwing away everything the operator typed.
      if (!isNil(preferredId) && resolved?.id === preferredId) {
        setCreating(false)
      }
    } catch (error) {
      if (isMounted()) {
        setLoadError(true)
      }
    } finally {
      if (isMounted()) {
        setLoaded(true)
      }
    }
  }

  useEffect(() => {
    mounted.current = true
    reload()
    return () => {
      mounted.current = false
    }
  }, [organizationId, dispatch])

  const selectConnection = (connectionId) => {
    if (connectionId === selectedId && !creating) {
      return
    }
    selectId(connectionId)
    setCreating(false)
    loadSyncState(
      connections.find((row) => row.id === connectionId),
      () => mounted.current
    )
  }

  // Leaving the create form returns to whatever was selected before it opened;
  // its sync state was never dropped, so there is nothing to refetch.
  const cancelCreate = () => setCreating(false)

  const connection = creating ? null : connections.find((row) => row.id === selectedId) ?? null

  return {
    connections,
    connection,
    selectedId,
    selectConnection,
    creating,
    startCreate: () => setCreating(true),
    cancelCreate,
    syncState,
    loaded,
    loadError,
    reload,
  }
}
